import React from 'react';
import { DateTime } from 'luxon';
import {
  Button,
  Popover,
  Row,
  Tooltip,
} from 'antd';
import {
  CopyOutlined,
  EditOutlined,
  ExclamationOutlined,
  NodeExpandOutlined,
} from '@ant-design/icons';
import { TaskHelpers } from 'ontraccr-common';

import { msToString } from './timecard.helpers';
import sortByString, { capitalizeFirstCharacter, sortByCode } from '../helpers/helpers';
import DateTimeWithZone from '../common/text/DateTimeWithZone';
import EditedClockInIcon from '../common/clock/EditedClockInIcon';
import Colors from '../constants/Colors';
import BorderlessButton from '../common/buttons/BorderlessButton';
import TimeCardApprovalButton, { APPROVE_TIMECARD_SUBMISSION, REJECT_TIMECARD_SUBMISSION } from './TimeCardApprovalButton';
import ArchivedUserWarning from './ArchivedUserWarning';
import { roundTotalRuntime } from '../helpers/time';
import { getEnteredViaTableText, sortByNameAndStartTime } from '../helpers/tasks';

// helpers functions / constants

export const EMPTY_TEXT = '--';

export const ITEMS_PER_SUMMARY_PAGE = 200;

const COLUMNS_TO_IGNORE = new Set(['edit', 'copy', 'approveReject', 'redistribute']);

const parseDayBoundary = (boundary) => (
  boundary?.dt
    ? boundary?.dt.toLocaleString(DateTime.TIME_SIMPLE)
    : EMPTY_TEXT
);

export const getFormattedListViewTask = ({
  task,
  costCodeMap,
  phaseMap,
  enableHourBasedTracking,
  unionClassMap,
  unionLocalMap,
  unionMap,
  entryDataMap,
  userMap,
  hoursBreakdown,
}) => {
  const {
    id,
    costcodeId,
    phaseId,
    hourBased,
    classId,
    userId,
    approvers,
  } = task;

  let totals = hoursBreakdown?.[userId]?.[id] ?? {};
  if (task.isPending) {
    const runtimes = TaskHelpers.getRuntimes(task, false);
    totals = {
      regularDuration: runtimes.regularTime,
      breakDuration: runtimes.breakTime,
      otDuration: runtimes.overtime,
      dtDuration: runtimes.doubleOT,
    };
  }

  const costCode = costCodeMap[costcodeId];
  const phase = phaseMap[phaseId]?.[0];

  const hideStartEndTime = hourBased && enableHourBasedTracking;
  const workClass = unionClassMap[classId];
  const localClass = unionLocalMap[workClass?.localId];
  const union = unionMap[localClass?.unionId];
  const taskUser = userMap[userId];
  const name = taskUser?.name;

  const formData = {};
  const { data: mapCustomData, divisionId: mapDivisionId } = entryDataMap[id] ?? {};
  const customData = mapCustomData ?? task.customData;
  const divisionId = mapDivisionId ?? task.divisionId;

  if (customData?.length) {
    customData.forEach(({ fields }) => {
      fields.forEach(({
        fieldId,
        response,
        type,
        title,
      }) => {
        formData[fieldId] = TaskHelpers.formatCustomDataForTimeTable({
          response,
          type,
          title,
        });
      });
    });
  }

  let totalDuration = (totals.regularDuration || 0)
    + (totals.otDuration || 0)
    + (totals.dtDuration || 0);

  if (task.type === 'break') {
    totalDuration = task.endTime && task.startTime
      ? task.endTime - task.startTime
      : null;
  }

  return {
    ...task,
    ...totals,
    ...formData,
    approvedBy: approvers
      ?.filter((approver) => !!approver)
      .map((approver) => approver.name)
      .join(','),
    costCode: costCode ? `${costCode?.code} - ${costCode?.name}` : EMPTY_TEXT,
    phase: phase?.name,
    hideStartEndTime,
    duration: totalDuration ? msToString(totalDuration) : EMPTY_TEXT,
    regularDuration: totals.regularDuration ? msToString(totals.regularDuration) : EMPTY_TEXT,
    breakDuration: totals.breakDuration ? msToString(totals.breakDuration) : EMPTY_TEXT,
    otDuration: totals.otDuration ? msToString(totals.otDuration) : EMPTY_TEXT,
    dtOtDuration: totals.dtDuration ? msToString(totals.dtDuration) : EMPTY_TEXT,
    ptoDuration: task.ptoEndTime ? msToString(task.ptoEndTime - task.ptoStartTime) : EMPTY_TEXT,
    union: union?.name,
    localClass: localClass?.name,
    workingClass: workClass?.name,
    type: capitalizeFirstCharacter(task.type),
    name,
    isArchivedUser: !!taskUser && !taskUser.active,
    divisionId,
    isNightShift: !!task.isNightShift,
  };
};

export const getCustomFieldMap = (customFields) => {
  const map = {};
  Object.values(customFields).forEach(({ divisionId, fields: sections }) => {
    sections?.forEach?.(({ fields }) => {
      fields?.forEach?.((subField) => {
        map[subField.id] = {
          title: subField.configProps.title,
          columns: subField.configProps.columns ?? [],
          divisionId,
        };
      });
    });
  });
  return map;
};

const getCustomFieldChildren = (customField, fieldId) => (
  customField?.columns?.map((column) => (
    {
      title: column.name,
      dataIndex: [fieldId, column.key],
      key: column.key,
      width: 150,
      render: (data) => (
        /* eslint-disable react/no-array-index-key */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 200,
            gap: 4,
          }}
        >
          {data?.map((value, idx) => (
            <div
              key={`${fieldId}-${column.key}-${idx}`}
              style={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflowX: 'hidden',
                height: '22px',
              }}
            >
              {value}
            </div>
          ))}
        </div>
      ),
    }
  ))
);

export const getTaskTime = (
  key,
  task,
) => {
  if (!task) return 0;
  switch (key) {
    case 'startTime':
      return task.startTime ?? 0;
    case 'endTime':
      return task.endTime ?? 0;
    case 'breakStartTime':
      return task.breakStartTime || (task.type === 'Break' ? task.startTime : 0);
    case 'breakEndTime':
      return task.breakEndTime || (task.type === 'Break' ? task.endTime : 0);
    case 'otStartTime':
      return task.otStartTime || (task.type === 'Overtime' ? task.startTime : 0);
    case 'otEndTime':
      return task.otEndTime || (task.type === 'Overtime' ? task.endTime : 0);
    case 'doubleOTStartTime':
      return task.doubleOTStartTime ?? 0;
    case 'doubleOTEndTime':
      return task.doubleOTEndTime ?? 0;
    case 'ptoStartTime':
      return task.ptoStartTime || (task.type === 'PTO' ? task.startTime : 0);
    case 'ptoEndTime':
      return task.ptoEndTime || (task.type === 'PTO' ? task.endTime : 0);
    default:
      return task[key];
  }
};

// columns
const startTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Regular Start Time
    </div>
  ),
  key: 'RegularStartTime',
  dataIndex: 'startTime',
  width: 80,
  sorter: handOffSort ? true : (a, b) => (a?.startTime ?? 0) - (b?.startTime ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (startTime, {
    type, hideStartEndTime, timezone, originalStart, preEditClockInTime,
  }) => (
    !hideStartEndTime && startTime && type !== 'Break' && type !== 'Overtime' && type !== 'PTO'
      ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DateTimeWithZone startTime={startTime} timezone={timezone} originalStart={originalStart} />
          {TaskHelpers.hasEditedClockIn({ preEditClockInTime }) && <EditedClockInIcon />}
        </div>
      )
      : EMPTY_TEXT
  ),
});

const endTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Regular End Time
    </div>
  ),
  key: 'RegularEndTime',
  dataIndex: 'endTime',
  width: 80,
  sorter: handOffSort ? true : (a, b) => (a?.endTime ?? 0) - (b?.endTime ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (endTime, {
    type, hideStartEndTime, timezone, originalEnd,
  }) => (
    !hideStartEndTime && endTime && type !== 'Break' && type !== 'Overtime' && type !== 'PTO'
      ? <DateTimeWithZone startTime={endTime} timezone={timezone} originalStart={originalEnd} />
      : EMPTY_TEXT
  ),
});

const breakStartTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Break Start Time
    </div>
  ),
  key: 'BreakStartTime',
  dataIndex: 'breakStartTime',
  width: 80,
  sorter: handOffSort ? true : (a, b) => (getTaskTime('breakStartTime', a) ?? 0) - (getTaskTime('breakStartTime', b) ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (_, {
    startTime,
    breakStartTime,
    type,
    hideStartEndTime,
    timezone,
  }) => {
    const relevantTime = getTaskTime('breakStartTime', { startTime, breakStartTime, type });
    return !hideStartEndTime && relevantTime
      ? <DateTimeWithZone startTime={relevantTime} timezone={timezone} />
      : EMPTY_TEXT;
  },
});

const breakEndTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Break End Time
    </div>
  ),
  key: 'BreakEndTime',
  dataIndex: 'breakEndTime',
  width: 80,
  sorter: handOffSort ? true : (a, b) => (getTaskTime('breakEndTime', a) ?? 0) - (getTaskTime('breakEndTime', b) ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (_, {
    endTime,
    breakEndTime,
    type,
    hideStartEndTime,
    timezone,
  }) => {
    const relevantTime = getTaskTime('breakEndTime', { endTime, breakEndTime, type });
    return !hideStartEndTime && relevantTime
      ? <DateTimeWithZone startTime={relevantTime} timezone={timezone} />
      : EMPTY_TEXT;
  },
});

const otStartTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      OT Start Time
    </div>
  ),
  key: 'OTStartTime',
  dataIndex: 'otStartTime',
  width: 80,
  sorter: handOffSort ? true : (a, b) => (getTaskTime('otStartTime', a) ?? 0) - (getTaskTime('otStartTime', b) ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (_, {
    startTime,
    otStartTime,
    type,
    hideStartEndTime,
    timezone,
  }) => {
    const relevantTime = getTaskTime('otStartTime', { startTime, otStartTime, type });
    return !hideStartEndTime && relevantTime
      ? <DateTimeWithZone startTime={relevantTime} timezone={timezone} />
      : EMPTY_TEXT;
  },
});

const otEndTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      OT End Time
    </div>
  ),
  key: 'OTEndTime',
  dataIndex: 'otEndTime',
  width: 80,
  sorter: handOffSort ? true : (a, b) => (getTaskTime('otEndTime', a) ?? 0) - (getTaskTime('otEndTime', b) ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (_, {
    endTime,
    otEndTime,
    type,
    hideStartEndTime,
    timezone,
  }) => {
    const relevantTime = getTaskTime('otEndTime', { endTime, otEndTime, type });
    return !hideStartEndTime && relevantTime
      ? <DateTimeWithZone startTime={relevantTime} timezone={timezone} />
      : EMPTY_TEXT;
  },
});

const doubleOTStartTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      DT OT Start Time
    </div>
  ),
  key: 'DoubleOTStartTime',
  dataIndex: 'doubleOTStartTime',
  width: 100,
  sorter: handOffSort ? true : (a, b) => (getTaskTime('doubleOTStartTime', a) ?? 0) - (getTaskTime('doubleOTStartTime', b) ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (_, {
    doubleOTStartTime,
    hideStartEndTime,
    timezone,
  }) => (!hideStartEndTime && doubleOTStartTime
    ? <DateTimeWithZone startTime={doubleOTStartTime} timezone={timezone} />
    : EMPTY_TEXT),
});

const doubleOTEndTimeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      DT OT End Time
    </div>
  ),
  key: 'DoubleOTEndTime',
  dataIndex: 'doubleOTEndTime',
  width: 100,
  sorter: handOffSort ? true : (a, b) => (getTaskTime('doubleOTEndTime', a) ?? 0) - (getTaskTime('doubleOTEndTime', b) ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (_, {
    doubleOTEndTime,
    hideStartEndTime,
    timezone,
  }) => (!hideStartEndTime && doubleOTEndTime
    ? <DateTimeWithZone startTime={doubleOTEndTime} timezone={timezone} />
    : EMPTY_TEXT),
});

const ptoDurationColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      PTO Duration
    </div>
  ),
  key: 'PTODuration',
  dataIndex: 'ptoDuration',
  width: 100,
  sorter: handOffSort
    ? true
    : (a, b) => (
      (getTaskTime('ptoEndTime', a) ?? 0) - (getTaskTime('ptoStartTime', a) ?? 0)
    ) - (
      (getTaskTime('ptoEndTime', b) ?? 0) - (getTaskTime('ptoStartTime', b) ?? 0)
    ),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const durationColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Duration
    </div>
  ),
  key: 'Duration',
  dataIndex: 'duration',
  width: 100,
  sorter: handOffSort
    ? true
    : (a, b) => ((a?.endTime ?? 0) - (a?.startTime ?? 0)) - ((b?.endTime ?? 0) - (b?.startTime ?? 0)),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const regularDuration = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Regular Duration
    </div>
  ),
  key: 'RegularDuration',
  dataIndex: 'regularDuration',
  width: 100,
  sorter: handOffSort
    ? true
    : (a, b) => (a.regularTime ?? 0) - (b.regularTime ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const breakDuration = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Break Duration
    </div>
  ),
  key: 'BreakDuration',
  dataIndex: 'breakDuration',
  width: 100,
  sorter: handOffSort ? true : (a, b) => (a.breakTime ?? 0) - (b.breakTime ?? 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const otDuration = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      OT Duration
    </div>
  ),
  key: 'OTDuration',
  dataIndex: 'otDuration',
  width: 100,
  sorter: handOffSort
    ? true
    : (a, b) => ((a?.overtime ?? 0)) - ((b?.overtime ?? 0)),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const dtOtDuration = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      DT OT Duration
    </div>
  ),
  key: 'DTOTDuration',
  dataIndex: 'dtOtDuration',
  width: 100,
  sorter: handOffSort
    ? true
    : (a, b) => ((a?.doubleOT ?? 0)) - ((b?.doubleOT ?? 0)),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const typeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Type
    </div>
  ),
  key: 'Type',
  dataIndex: 'type',
  width: 80,
  sorter: handOffSort ? true : sortByString('type'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (type) => {
    if (type === 'Pto') return 'PTO';
    return type;
  },
});

const approvedByColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Approved By
    </div>
  ),
  key: 'ApprovedBy',
  dataIndex: 'approvedBy',
  width: 120,
  sorter: handOffSort ? true : sortByString('approvedBy'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const phaseColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Phase
    </div>
  ),
  key: 'Phase',
  width: 100,
  dataIndex: 'phase',
  sorter: handOffSort ? true : sortByString('phase'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const costCodeColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Cost Code
    </div>
  ),
  key: 'CostCode',
  dataIndex: 'costCode',
  width: 120,
  sorter: handOffSort ? true : sortByCode('costcode_code'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const unionColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Union
    </div>
  ),
  key: 'Union',
  width: 100,
  dataIndex: 'union',
  sorter: handOffSort ? true : sortByString('union'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const localClassColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Local Class
    </div>
  ),
  key: 'LocalClass',
  dataIndex: 'localClass',
  width: 120,
  sorter: handOffSort ? true : sortByString('localClass'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const workClassColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Work Class
    </div>
  ),
  key: 'WorkClass',
  width: 120,
  dataIndex: 'workingClass',
  sorter: handOffSort ? true : sortByString('workingClass'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const notesColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Notes
    </div>
  ),
  key: 'Notes',
  dataIndex: 'note',
  width: 200,
  sorter: handOffSort ? true : sortByString('note'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
});

const nightShiftColumn = (handOffSort) => ({
  title: (
    <div className="timecard-header-cell">
      Night Shift
    </div>
  ),
  key: 'isNightShift',
  dataIndex: 'isNightShift',
  width: 100,
  align: 'center',
  sorter: handOffSort ? true : (a, b) => (a?.isNightShift ? 1 : 0) - (b?.isNightShift ? 1 : 0),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (isNightShift) => (isNightShift ? 'Yes' : '-'),
});

const nameColumn = {
  title: (
    <div className="timecard-header-cell">
       Name
    </div>
  ),
  key: 'name',
  dataIndex: 'name',
  width: 120,
  sorter: true,
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  render: (name, record) => (
    <span>
      {name}
      {record?.isArchivedUser && <ArchivedUserWarning />}
    </span>
  ),
};

const summaryDateColumn = {
  title: (
    <div className="timecard-header-cell">
      Date
    </div>
  ),
  key: 'date',
  dataIndex: 'date',
  width: 150,
  render: (_date, record) => {
    const taskData = TaskHelpers.getTaskDate(record);
    return taskData?.toLocaleString?.(DateTime.DATE_FULL) ?? '';
  },
  sorter: true,
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
};

const dayDateColumn = {
  title: 'Date',
  dataIndex: 'key',
  key: 'day',
  width: 150,
};

const dayTotalTimeColumn = ({ roundingInterval, roundingType, roundingSetting }) => ({
  title: 'Total Time',
  dataIndex: 'total',
  key: 'totalTime',
  width: 100,
  render: (_, task) => (
    <span>
      {msToString(
        roundTotalRuntime(
          task.total + (task.totalPTO || 0),
          roundingInterval,
          roundingType,
          roundingSetting,
        ),
      )}
    </span>
  ),
});

const dayStartTime = {
  title: 'Start Time',
  dataIndex: 'minTS',
  key: 'StartTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayEndTime = {
  title: 'End Time',
  dataIndex: 'maxTS',
  key: 'EndTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayRegularStartTime = {
  title: 'Regular Start Time',
  dataIndex: 'minRegularTS',
  key: 'RegularStartTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayRegularEndTime = {
  title: 'Regular End Time',
  dataIndex: 'maxRegularTS',
  key: 'RegularEndTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayBreakStartTime = {
  title: 'Break Start',
  dataIndex: 'minBreakTS',
  key: 'BreakStartTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayBreakEndTime = {
  title: 'Break End',
  dataIndex: 'maxBreakTS',
  key: 'BreakEndTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayOTStartTime = {
  title: 'OT Start',
  dataIndex: 'minOvertimeTS',
  key: 'OTStartTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayOTEndTime = {
  title: 'OT End',
  dataIndex: 'maxOvertimeTS',
  key: 'OTEndTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayDoubleOTStartTime = {
  title: 'DT OT Start',
  dataIndex: 'minDoubleOvertimeTS',
  key: 'DoubleOTStartTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayDoubleOTEndTime = {
  title: 'DT OT End',
  dataIndex: 'maxDoubleOvertimeTS',
  key: 'DoubleOTEndTime',
  width: 80,
  render: (data) => (
    <span>
      {parseDayBoundary(data)}
    </span>
  ),
};

const dayPTODuration = {
  title: 'PTO Duration',
  dataIndex: 'totalPTO',
  key: 'ptoDuration',
  width: 100,
  render: (totalPTO) => (
    <span>
      {msToString(totalPTO || 0)}
    </span>
  ),
};

const violationColumn = ({
  isSummary = false,
}) => {
  const baseOpts = {
    title: (
      <div className="timecard-header-cell">
        Violation
      </div>
    ),
    dataIndex: 'hasViolation',
    key: 'hasViolation',
    width: 100,
    align: 'center',
    render: (_, record) => {
      let hasViolation;
      let hasAutoClockedOut;
      let hasOutsideWorkingHours;
      if (isSummary) {
        hasViolation = record.geofenceViolation;
        hasAutoClockedOut = record.isAutoClockOut;
        hasOutsideWorkingHours = record.outsideWorkingHours;
      } else {
        hasViolation = record.dayTasks.some((task) => task.geofenceViolation);
        hasAutoClockedOut = record.dayTasks.some((task) => task.isAutoClockOut);
        hasOutsideWorkingHours = record.dayTasks.some((task) => task.outsideWorkingHours);
      }
      const content = [];
      if (hasViolation) content.push('This Time Card has a geofence breach');
      if (hasAutoClockedOut) content.push('This Time Card has an automatic clock-out');
      if (hasOutsideWorkingHours) content.push('This Time Card is outside of working hours');
      if (hasViolation || hasAutoClockedOut || hasOutsideWorkingHours) {
        return (
          <Popover
            trigger="hover"
            content={content.join('\n')}
            placement="topRight"
          >
            <ExclamationOutlined
              style={{
                color: 'white',
                backgroundColor: Colors.ONTRACCR_RED,
                padding: 1,
                height: 12,
                width: 12,
                borderRadius: 6,
                fontSize: 10,
              }}
            />
          </Popover>
        );
      }
      return null;
    },
  };

  let sortOpts = {};

  if (isSummary) {
    sortOpts = {
      sorter: (a, b) => {
        const aAuto = a.isAutoClockOut ?? a.dayTasks?.some((t) => t.isAutoClockOut);
        const bAuto = b.isAutoClockOut ?? b.dayTasks?.some((t) => t.isAutoClockOut);
        const aViolation = (a.geofenceViolation || aAuto || a.outsideWorkingHours) ? 1 : 0;
        const bViolation = (b.geofenceViolation || bAuto || b.outsideWorkingHours) ? 1 : 0;

        return aViolation - bViolation;
      },
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    };
  }

  return { ...baseOpts, ...sortOpts };
};

const rejectionColumn = {
  title: 'Rejection',
  dataIndex: 'hasRejection',
  key: 'hasRejection',
  width: 100,
  align: 'center',
  render: (_, record) => {
    const latestTimeCardEvent = record.dayHistory[record.dayHistory.length - 1] ?? {};
    const isRecentlyRejected = latestTimeCardEvent?.type === 'rejected';
    if (isRecentlyRejected) {
      const { timestamp = 0, note } = latestTimeCardEvent;
      const date = DateTime.fromMillis(timestamp).toLocaleString(DateTime.DATETIME_MED);
      const noteMsg = note ? ` with a note: ${note}` : '.';
      return (
        <Popover
          trigger="hover"
          placement="topRight"
          content={(
            <div>
              <Row>
                <b>{`Timecard Rejected - ${date}`}</b>
              </Row>
              <Row>
                {`This Timecard was rejected${noteMsg}`}
              </Row>
            </div>
          )}
        >
          <ExclamationOutlined
            style={{
              color: 'white',
              backgroundColor: Colors.ONTRACCR_RED,
              padding: 1,
              height: 12,
              width: 12,
              borderRadius: 6,
              fontSize: 10,
            }}
          />
        </Popover>
      );
    }
    return null;
  },
};

const statusColumn = (handOffSort) => ({
  title: 'Status',
  key: 'Status',
  dataIndex: 'state',
  sorter: handOffSort ? true : sortByString('state'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
  width: 120,
  render: (status, record) => {
    let fillColor;
    let text;

    let taskStatus = status;
    if (record?.isPending) taskStatus = 'pending';

    switch (taskStatus) {
      case 'submitted':
        fillColor = 'yellow';
        text = 'Submitted';
        break;
      case 'approved':
        fillColor = 'green';
        text = 'Approved';
        break;
      case 'pending':
        fillColor = 'orange';
        text = 'Pending';
        break;
      default:
        fillColor = 'black';
        text = 'Unsubmitted';
        break;
    }

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        <svg
          height="15"
          width="15"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: 5 }}
        >
          <circle cx="10" cy="10" r="10" fill={fillColor} stroke="black" strokeWidth="1" />
        </svg>
        { text }
      </div>
    );
  },
});

const approveRejectColumn = ({
  userId = null,
  isSummary = false,
}) => ({
  title: '',
  key: 'approveReject',
  dataIndex: 'approveReject',
  width: 180,
  fixed: isSummary ? 'right' : undefined,
  render: (_, record) => (
    <div style={{ display: 'flex' }}>
      <TimeCardApprovalButton
        record={record}
        type={APPROVE_TIMECARD_SUBMISSION}
        userId={isSummary ? record.userId : userId}
        isSummary={isSummary}
      />
      <TimeCardApprovalButton
        record={record}
        type={REJECT_TIMECARD_SUBMISSION}
        userId={isSummary ? record.userId : userId}
        isSummary={isSummary}
      />
    </div>
  ),
});

const editColumn = ({
  onEdit,
  isSummary = false,
}) => ({
  title: (
    <div className='timecard-header-cell' />
  ),
  dataIndex: 'edit',
  key: 'edit',
  width: 50,
  align: 'center',
  render: (_, record) => {
    let canEditCards;
    if (isSummary) {
      canEditCards = record.canEdit;
    } else {
      canEditCards = record.dayTasks.some((task) => task.canEdit);
    }

    if (!canEditCards) return null;
    return (
      <BorderlessButton
        style={{ padding: 0, width: 20 }}
        iconNode={<EditOutlined style={{ marginLeft: 0 }} />}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          return isSummary ? await onEdit(record.id) : await onEdit(record.key);
        }}
      />
    );
  },
});

const copyColumn = ({
  onCopy,
}) => ({
  title: '',
  dataIndex: 'copy',
  key: 'copy',
  width: 50,
  align: 'center',
  fixed: 'right',
  render: (_, record) => {
    const { canEdit } = record;

    if (!canEdit) return null;

    return (
      <BorderlessButton
        style={{ padding: 0, width: 20 }}
        iconNode={<CopyOutlined style={{ marginLeft: 0 }} />}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCopy(record.id);
        }}
      />
    );
  },
});

const redistributeColumn = ({
  onRedistributeHours,
}) => ({
  title: (
    <div className="timecard-header-cell" />
  ),
  dataIndex: 'redistribute',
  key: 'redistribute',
  width: 50,
  align: 'center',
  render: (_, record) => {
    const { state } = record;

    if (state === 'approval' || TaskHelpers.isOnlyPTO(record)) return null;

    return (
      <Tooltip title="Redistribute Hours">
        <BorderlessButton
          style={{ padding: 0, width: 20 }}
          iconNode={<NodeExpandOutlined style={{ marginLeft: 0 }} />}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await onRedistributeHours(record.id);
          }}
        />
      </Tooltip>
    );
  },
});

const projectColumn = (({ t, handOffSort }) => ({
  title: (
    <div className="timecard-header-cell">
      {t('Project')}
    </div>
  ),
  key: 'Project',
  dataIndex: 'projectName',
  width: 100,
  sorter: handOffSort ? true : sortByString('projectName'),
  showSorterTooltip: false,
  sortDirections: ['descend', 'ascend'],
}));

const enteredViaColumn = ({ onFormClick }) => ({
  title: (
    <div className="timecard-header-cell">
      Entered Via
    </div>
  ),
  key: 'enteredVia',
  width: 200,
  render: (_, record) => {
    const {
      enteredViaMetadata: {
        entered: {
          formId,
        } = {},
      } = {},
    } = record;

    if (formId) {
      return (
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onFormClick(formId);
          }}
          style={{
            borderWidth: 0,
            backgroundColor: 'inherit',
            boxShadow: 'none',
            width: '100%',
            padding: 0,
            whiteSpace: 'normal',
            textAlign: 'left',
            height: '100%',
          }}
        >
          {getEnteredViaTableText(record.enteredViaMetadata)}
        </Button>
      );
    }

    return getEnteredViaTableText(record.enteredViaMetadata);
  },
});

const cardColumn = ({ onCardColumnClick}) => ({
  title: (
    <div className="timecard-header-cell">
      Card
    </div>
  ),
  key: 'card',
  width: 150,
  render: (_, record) => {
    if (!record.cards?.length) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {record.cards.map((card, index) => {
          return (
            <Button
              key={`${card.cardId}-${record.id}`}
              type="button"
              className="link-button"
              style={{ width: 'fit-content' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCardColumnClick?.(card);
              }}
            >
              <span className="link-button-text" style={{ width: 'fit-content' }}>
                {`${card.title}${record.cards[index + 1] ? ',' : ''}`}
              </span>
            </Button>
          );
        })}
      </div>
    );
  },
});

// table columns

export const getSummaryViewColumns = ({
  t,
  onSummaryTaskModify,
  onFormClick,
  isApprovals,
  divisionIds,
  customFieldMap,
  selectedTimeCardSummaryColumns,
  selectedCustomFieldColumns,
  onCardColumnClick,
  enableNightShiftPremium = false,
}) => {
  let baseCols = [
    editColumn({ onEdit: onSummaryTaskModify('edit'), isSummary: true }),
    redistributeColumn({ onRedistributeHours: onSummaryTaskModify('redistribute') }),
    nameColumn,
    summaryDateColumn,
    startTimeColumn(true),
    endTimeColumn(true),
    breakStartTimeColumn(true),
    breakEndTimeColumn(true),
    otStartTimeColumn(true),
    otEndTimeColumn(true),
    doubleOTStartTimeColumn(true),
    doubleOTEndTimeColumn(true),
    durationColumn(true),
    regularDuration(true),
    breakDuration(),
    otDuration(true),
    dtOtDuration(true),
    ptoDurationColumn(true),
    typeColumn(true),
    projectColumn({ t, handOffSort: true }),
    phaseColumn(true),
    costCodeColumn(true),
    unionColumn(true),
    localClassColumn(true),
    workClassColumn(true),
    notesColumn(true),
    enteredViaColumn({ onFormClick }),
    approvedByColumn(true),
    violationColumn({ isSummary: true }),
    cardColumn({ onCardColumnClick }),
  ];

  const selectedColumns = selectedTimeCardSummaryColumns || [];

  if (enableNightShiftPremium) {
    baseCols.push(nightShiftColumn(true));
    if (!selectedTimeCardSummaryColumns) {
      selectedColumns.push('isNightShift');
    }
  }

  if (selectedCustomFieldColumns) {
    selectedCustomFieldColumns.forEach((fieldId) => {
      const customField = customFieldMap[fieldId];

      if (customField && divisionIds.has(customField.divisionId)) {
        const children = getCustomFieldChildren(customField, fieldId);
        baseCols.push({
          title: (
            <div className="timecard-header-cell">
              {customField.title}
            </div>
          ),
          dataIndex: fieldId,
          key: fieldId,
          width: 100,
          children,
        });
        selectedColumns.push(fieldId);
      }
    });
  }

  if (!isApprovals) {
    baseCols.push(statusColumn(true));
    baseCols.push(copyColumn({ onCopy: onSummaryTaskModify('copy') }));
  } else {
    baseCols.push(approveRejectColumn({ isSummary: true }));
  }

  if (selectedTimeCardSummaryColumns) {
    const columnMap = new Set(selectedColumns);
    baseCols = baseCols.filter((item) => {
      if (COLUMNS_TO_IGNORE.has(item.key)) return true;
      return columnMap.has(item.key);
    });
  }

  return baseCols;
};

export const getListViewDayColumns = ({
  roundingInterval,
  roundingType,
  roundingSetting,
  selectedDayEntryColumns,
  isApprovals,
  onEdit,
  userId,
}) => {
  const baseCols = [
    dayDateColumn,
    dayTotalTimeColumn({ roundingInterval, roundingType, roundingSetting }),
    dayStartTime,
    dayEndTime,
    dayRegularStartTime,
    dayRegularEndTime,
    dayBreakStartTime,
    dayBreakEndTime,
    dayOTStartTime,
    dayOTEndTime,
    dayDoubleOTStartTime,
    dayDoubleOTEndTime,
    dayPTODuration,
    violationColumn({ isSummary: false }),
    rejectionColumn,
  ];

  let relevantItems = [...baseCols];

  if (selectedDayEntryColumns) {
    const columnMap = new Set(selectedDayEntryColumns);
    relevantItems = baseCols.filter((item) => columnMap.has(item.key));
  }

  if (isApprovals) {
    relevantItems.push(approveRejectColumn({ userId, isSummary: false }));
  }

  relevantItems.push(editColumn({ onEdit }));

  return relevantItems;
};

export const getListViewExpandedColumns = ({
  t,
  selectedTimeCardEntryColumns,
  selectedCustomFieldColumns,
  divisionIds,
  customFieldMap,
  onFormClick,
  onCardColumnClick,
  enableNightShiftPremium = false,
}) => {
  let baseCols = [
    startTimeColumn(),
    endTimeColumn(),
    breakStartTimeColumn(),
    breakEndTimeColumn(),
    otStartTimeColumn(),
    otEndTimeColumn(),
    doubleOTStartTimeColumn(),
    doubleOTEndTimeColumn(),
    ptoDurationColumn(),
    durationColumn(),
    regularDuration(),
    breakDuration(),
    otDuration(),
    dtOtDuration(),
    typeColumn(),
    projectColumn({ t }),
    phaseColumn(),
    costCodeColumn(),
    unionColumn(),
    localClassColumn(),
    workClassColumn(),
    notesColumn(),
    enteredViaColumn({ onFormClick }),
    approvedByColumn(),
    cardColumn({ onCardColumnClick }),
  ];

  const selectedColumns = selectedTimeCardEntryColumns || [];

  if (enableNightShiftPremium) {
    baseCols.push(nightShiftColumn());
    if (!selectedTimeCardEntryColumns) {
      selectedColumns.push('isNightShift');
    }
  }

  if (selectedCustomFieldColumns) {
    selectedCustomFieldColumns.forEach((fieldId) => {
      const customField = customFieldMap[fieldId];

      if (customField && divisionIds.has(customField.divisionId)) {
        const children = getCustomFieldChildren(customField, fieldId);
        baseCols.push({
          title: customField.title,
          dataIndex: fieldId,
          key: fieldId,
          width: 100,
          children,
        });
        selectedColumns.push(fieldId);
      }
    });
  }

  baseCols.push(statusColumn());

  if (selectedTimeCardEntryColumns) {
    const columnMap = new Set(selectedColumns);
    baseCols = baseCols.filter((item) => columnMap.has(item.key));
  }

  return baseCols;
};

export const sortEntries = ({
  startDt,
  endDt,
  payrollTasks = [],
  userMap = {},
}) => {
  if (endDt < startDt) return [];
  const numDays = Math.ceil(endDt.diff(startDt, 'days').as('days'));
  if (Number.isNaN(numDays)) return [];
  const daysArr = new Array(numDays + 1).fill().map(() => []);
  /*
      Need to sort by startTime per day to ensure the ordering
      of hour based entries
  */
  let dayEntries = [];
  let lastDay = null;
  const numEntries = payrollTasks.length;
  payrollTasks.forEach((entry, idx) => {
    const entryDT = TaskHelpers.getTaskDate(entry);
    const isLast = idx === (numEntries - 1);
    const newDate = entryDT?.toSQLDate() !== lastDay?.toSQLDate();
    if (newDate) {
      if (lastDay && dayEntries.length) {
        dayEntries.sort(sortByNameAndStartTime(userMap));
        const index = lastDay?.diff(startDt, 'days').as('days') ?? 0;
        daysArr[index]?.push?.(...dayEntries);
      }
      lastDay = entryDT?.startOf('day') ?? null;
      dayEntries = [entry];
    } else {
      dayEntries.push(entry);
    }

    if (isLast) {
      dayEntries.sort(sortByNameAndStartTime(userMap));
      const index = lastDay?.diff(startDt, 'days').as('days') ?? 0;
      daysArr[index]?.push?.(...dayEntries);
    }
  });
  return daysArr.flat();
};

export const stringSortColumns = new Set([
  'type',
  'approvedBy',
  'phase',
  'union',
  'localClass',
  'workingClass',
  'note',
  'name',
  'projectName',
]);

export const taskTimeColumns = new Set([
  'startTime',
  'endTime',
  'breakStartTime',
  'breakEndTime',
  'otStartTime',
  'otEndTime',
  'doubleOTStartTime',
  'doubleOTEndTime',
]);

export default {};
